import React, { useMemo } from 'react'
import {
  useWorkOrderGeneralConfigQuery,
  WorkOrderGeneralConfigResponse,
  workOrderListQueryOpts
} from '~/api/work_order'
import { lazyMsg, Msg } from '~/common/localizationUtils'
import { FILTER_STATUSES, StatusEnum, userRoles, woStatus, woStatusConfig } from '~/work_order_list/types_enums'
import { ShiftStatus } from '~/api/shifts'
import { defineFiltersWithContext } from '~/components/v2/ResultsLayout/Filters/FiltersProvider'
import { filter } from '~/components/v2/ResultsLayout/Filters/Filters'
import { FilterOption } from '~/components/v2/ResultsLayout/Filters/types'
import { UserPreferenceNamespace } from '~/api/userPreferences'
import { defineActionBar } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.hook'
import { defineListData } from '~/components/v2/ResultsLayout/ListQueryProvider/ListQueryProvider'
import { useJobConfig } from '~/api/jobs'
import { useWorkdayContingentEnabled } from '~/api/workday'
import { useMultiPmoEnabled } from '~/api/programTeam'
import { bulkEligibleWorkOrderIdsOpts } from '~/api/work_order/work_order'
import { AccountMeResponse, useAccountMeQuery } from '~/api/accounts'
import { programTeamEntityFilter } from '~/dashboards/filters/config'

export const getWOFilterOptionFromConfig = (val: number) => {
  const config: StatusEnum = woStatusConfig[val]
  return { id: val, value: String(val), text: <Msg id={config.l10n_key} /> }
}
export const getWoStatusFilter = () => ({
  id: 'status',
  allowedFor: [userRoles.All],
  type: 'list',
  title: lazyMsg('work_order.status_title.current_status'),
  config: { visibleItemCount: 13 },
  options: FILTER_STATUSES.map(getWOFilterOptionFromConfig),
  defaultValue: [woStatus.ACTIVE]
})
export const locationFilter = {
  id: 'location',
  allowedFor: [userRoles.All],
  type: 'list',
  config: { maxCheckableCount: 100, isVirtualized: true },
  optionsFromApi: {
    url: '/api/v2/work_sites',
    pageSize: 100,
    searchParamKey: 'name',
    transform: "results.{'id':id, 'text':name, 'value':id.$string()}[]"
  },
  title: lazyMsg('term.location', { count: 1 })
}
export const resourceManagerFilter = {
  id: 'rm',
  allowedFor: [userRoles.All],
  type: 'list',
  config: { maxCheckableCount: 100, isVirtualized: true },
  optionsFromApi: {
    url: '/api/v2/employer_manager?type=rm',
    pageSize: 100,
    searchParamKey: 'name',
    transform: "results.{'id':id, 'text':full_name, 'value':id.$string()}[]"
  },
  title: lazyMsg('term.resource_manager', { count: 1 })
}
export const getVendorFilter = () => ({
  id: 'vendor',
  allowedFor: [userRoles.Employer],
  type: 'list',
  config: { maxCheckableCount: 100 },
  optionsFromApi: {
    transform: "results.{'id':id, 'text':company_name, 'value':id.$string()}[]",
    pageSize: 100,
    searchParamKey: 'search',
    url: '/api/v2/vendor_entities'
  },
  title: lazyMsg('term.vendor', { count: 1 })
})
export const getBusinessUnitFilter = () => ({
  id: 'business_unit',
  allowedFor: [userRoles.Employer],
  type: 'bu',
  title: lazyMsg('term.business_unit', { count: 1 })
})

export const actionBarDef = defineActionBar({
  searchPlaceholder: <Msg id="common.ui.search_term" term={<Msg id="term.workorder" />} />,
  orderings: [
    {
      value: 'contractor',
      label: <Msg id="common.ui.sort.alphabet_ascending" term={<Msg id="work_order.contractor_name" count={1} />} />
    },
    {
      value: '-contractor',
      label: <Msg id="common.ui.sort.alphabet_descending" term={<Msg id="work_order.contractor_name" count={1} />} />
    },
    {
      value: 'annotated_end_date',
      label: <Msg id="common.ui.sort.date_ascending" term={<Msg id="common.ui.end_date" />} />
    },
    {
      value: '-annotated_end_date',
      label: <Msg id="common.ui.sort.date_descending" term={<Msg id="common.ui.end_date" />} />
    },
    {
      value: '-id',
      label: <Msg id="common.ui.sort_term_high_low" term={<Msg id="term.id" count={1} />} />
    },
    {
      value: 'id',
      label: <Msg id="common.ui.sort_term_low_high" term={<Msg id="term.id" count={1} />} />
    },
    {
      value: 'last_modified',
      label: <Msg id="common.ui.last_modified" />
    },
    {
      value: 'annotated_start_date',
      label: <Msg id="common.ui.sort.date_ascending" term={<Msg id="common.ui.start_date" />} />
    },
    {
      value: '-annotated_start_date',
      label: <Msg id="common.ui.sort.date_descending" term={<Msg id="common.ui.start_date" />} />
    },
    {
      value: 'date_created',
      label: <Msg id="common.ui.date_created_date" />
    }
  ],
  defaultOrdering: '-id',
  hasDisplayModeButtons: true,
  defaultUserPreferenceNamespace: UserPreferenceNamespace.WORK_ORDER,
  hasVisibleFieldsButton: true
} as const)

export const filtersDef = defineFiltersWithContext(
  ({
    woGeneralConfig,
    userAccount
  }: {
    woGeneralConfig?: WorkOrderGeneralConfigResponse
    userAccount?: AccountMeResponse
  }) => {
    const { data: jobConfig, isLoading: jobLoading } = useJobConfig()
    const { data: isWorkdayContingentEnabled, isLoading: contingentLoading } = useWorkdayContingentEnabled()
    const { data: multiPmoEnabled, isLoading: multiPmoLoading } = useMultiPmoEnabled()
    const config = useMemo(() => {
      const baseConfig = {
        status: filter.checkboxList.asStrings({
          id: 'status_val',
          allowedFor: [userRoles.All],
          title: lazyMsg('work_order.status_title.current_status'),
          config: { visibleItemCount: 13 },
          options: FILTER_STATUSES.map(getWOFilterOptionFromConfig),
          defaultValue: [woStatus.ACTIVE.toString()]
        }),
        modificationStatus: filter.checkboxList.asStrings({
          id: 'modification_status',
          allowedFor: [userRoles.All],
          type: 'list',
          title: lazyMsg('work_order.status_title.modification_status'),
          options: [
            {
              id: 'vendor_approval_pending',
              text: lazyMsg('work_order.status_display_name.vendor_approval_pending'),
              value: 'vendor_approval_pending'
            },
            {
              id: 'internal_approval_pending',
              text: lazyMsg('work_order.status_display_name.internal_approval_pending'),
              value: 'internal_approval_pending'
            }
          ]
        }),
        location: filter.checkboxList.asStrings({
          id: 'location',
          title: lazyMsg('term.location', { count: 1 }),
          defaultIsOpen: false,
          allowedFor: [userRoles.All],
          config: { maxCheckableCount: 100, isVirtualized: true },
          optionsFromApi: {
            url: '/api/v2/work_sites',
            pageSize: 100,
            searchParamKey: 'name',
            transform: "results.{'id':id, 'text':name, 'value':id.$string()}[]"
          }
        }),
        region: filter.checkboxList.asStrings({
          id: 'region',
          allowedFor: [userRoles.All],
          title: lazyMsg('term.region', { count: 1 }),
          type: 'list',
          listType: 'number',
          defaultIsOpen: false,
          config: { containerMaxHeight: 600 },
          optionsFromApi: {
            pageSize: 100,
            searchParamKey: 'name',
            url: '/api/v2/regions',
            transform: "results.{'text':name, 'value':id}[]"
          }
        }),
        resourceManager: filter.checkboxList.asStrings({
          id: 'rm',
          allowedFor: [userRoles.All],
          type: 'list',
          config: { maxCheckableCount: 100, isVirtualized: true },
          optionsFromApi: {
            url: '/api/v2/employer_manager?type=rm',
            pageSize: 100,
            searchParamKey: 'name',
            transform: "results.{'id':id, 'text':full_name, 'value':id.$string()}[]"
          },
          title: lazyMsg('term.resource_manager', { count: 1 })
        }),
        sourceType: filter.checkboxList.asStrings({
          id: 'source_type',
          allowedFor: [userRoles.All],
          type: 'list',
          title: lazyMsg('term.workorder.field.source_type'),
          options: [
            { id: 'direct_hire', text: lazyMsg('term.direct_hire'), value: 'Direct Hire' },
            { id: 'directed', text: lazyMsg('term.workorder.field.source_type.value_directed'), value: 'Directed' },
            { id: 'payrolled', text: lazyMsg('term.workorder.field.source_type.value_payrolled'), value: 'Payrolled' },
            {
              id: 'statement_of_work',
              text: lazyMsg('term.workorder.field.source_type.value_sow'),
              value: 'Statement of Work'
            },
            {
              id: 'supplier_sourced',
              text: lazyMsg('term.candidate_source_type.value_supplier_sourced'),
              value: 'Supplier sourced'
            },
            woGeneralConfig?.include_wpm && {
              id: 'worker_profile',
              text: lazyMsg('term.worker_profile', { count: 1 }),
              value: 'Worker Profile'
            }
          ].filter(Boolean) as FilterOption<string>[]
        }),
        payType: filter.checkboxList.asStrings({
          id: 'pay_type',
          allowedFor: [userRoles.All],
          title: lazyMsg('term.pay_type', { count: 1 }),
          type: 'list',
          listType: 'number',
          defaultIsOpen: false,
          config: { containerMaxHeight: 600 },
          optionsFromApi: {
            isSearchable: false,
            url: '/api/v2/jobs/pay_types',
            transform: "results.{'text':text, 'value':value}[]"
          }
        }),
        vendor: filter.checkboxList.asStrings({
          id: 'vendor',
          allowedFor: [userRoles.Employer],
          type: 'list',
          config: { maxCheckableCount: 100 },
          optionsFromApi: {
            transform: "results.{'id':id, 'text':company_name, 'value':id.$string()}[]",
            pageSize: 100,
            searchParamKey: 'search',
            url: '/api/v2/vendor_entities'
          },
          title: lazyMsg('term.vendor', { count: 1 })
        }),
        businessUnit: filter.buPicker({
          v2: true,
          id: 'business_unit',
          allowedFor: [userRoles.Employer],
          title: lazyMsg('term.business_unit', { count: 1 })
        }),
        startDate: filter.dateRange({
          id: 'start_date',
          allowedFor: [userRoles.All],
          title: lazyMsg('common.ui.start_date')
        }),
        endDate: filter.dateRange({
          id: 'end_date',
          allowedFor: [userRoles.All],
          title: lazyMsg('common.ui.end_date')
        }),
        shiftStrategy: filter.checkboxList.asStrings({
          id: 'shift_strategy',
          allowedFor: [userRoles.All],
          title: lazyMsg('shifts.shift_strategy'),
          type: 'list',
          listType: 'number',
          defaultIsOpen: false,
          config: { containerMaxHeight: 600 },
          optionsFromApi: {
            isSearchable: false,
            url: '/api/v2/shifts/strategies/',
            transform: "results.{'text':name, 'value':id}[]"
          }
        }),
        shifts: filter.checkboxList.asStrings({
          id: 'shifts',
          allowedFor: [userRoles.All],
          title: lazyMsg('term.shift', { count: 0 }),
          type: 'list',
          config: { maxCheckableCount: 100 },
          optionsFromApi: {
            transform: "results.{'id':id, 'text':name, 'value':id.$string()}[]",
            pageSize: 100,
            searchParamKey: 'search',
            url: `/api/v2/shifts/list/?status=${ShiftStatus.ACTIVE}&has_work_orders=True`
          }
        }),
        programTeam: filter.checkboxList.asStrings({
          id: 'program_team',
          type: 'list',
          title: lazyMsg('term.program_team'),
          listType: 'number',
          allowedFor: [userRoles.All],
          config: { maxCheckableCount: 100, isVirtualized: true, containerMaxHeight: 600 },
          optionsFromApi: {
            pageSize: 100,
            searchParamKey: 'query',
            url: '/api/v2/employer_manager/?type=program_team&include_inactive=0',
            transform: "results.{'text':full_name, 'value':id}[]"
          }
        }),
        programTeamEntity: filter.checkboxList.asNumbers(programTeamEntityFilter as any),
        integrations: filter.checkboxList.asStrings({
          id: 'integrations',
          allowedFor: [userRoles.All],
          title: lazyMsg('term.integration', { count: 2 }),
          type: 'list',
          optionsFromApi: {
            isSearchable: false,
            url: '/api/v2/connectors/work-order-filters',
            transform: "results.{'text':text, 'value':value, 'toolTip':toolTip}[]"
          }
        }),
        jobCategory: filter.checkboxList.asStrings({
          id: 'job_category',
          allowedFor: [userRoles.All],
          type: 'list',
          config: { maxCheckableCount: 100 },
          optionsFromApi: {
            transform: "results.{'id':id, 'text':name, 'value':id.$string()}[]",
            pageSize: 100,
            searchParamKey: 'name',
            url: '/api/v2/jobs/categories'
          },
          title: lazyMsg('term.job.field.job_category', { count: 1 })
        }),
        jobTemplate: filter.checkboxList.asStrings({
          id: 'job_template',
          allowedFor: [userRoles.All],
          type: 'list',
          config: { maxCheckableCount: 100 },
          optionsFromApi: {
            transform: "results.{'id':id, 'text':title, 'value':id.$string()}[]",
            pageSize: 100,
            searchParamKey: 'title',
            url: '/api/v2/job_titles/title_search'
          },
          title: lazyMsg('term.job_template', { count: 1 })
        }),
        calculationPack: filter.checkboxList.asStrings({
          id: 'formula_group',
          allowedFor: [userRoles.All],
          title: lazyMsg('term.calculation_pack', { count: 1 }),
          type: 'list',
          config: { maxCheckableCount: 100 },
          optionsFromApi: {
            transform: "results.{'id':id, 'text':name, 'value':id.$string()}[]",
            pageSize: 100,
            searchParamKey: 'search',
            url: `/api/calculation_engine/calculation_pack_options/`
          }
        })
      }
      return Object.fromEntries(
        Object.entries(baseConfig).filter(([_, filterDefinition]) => {
          if (!filterDefinition || !Array.isArray(filterDefinition.allowedFor)) {
            return false
          }

          const isAllowedByRole =
            filterDefinition.allowedFor.includes(userRoles.All) ||
            (userAccount != null && filterDefinition.allowedFor.includes(userAccount.role))

          let isConditionallyVisible = true

          if (filterDefinition.id === 'integrations') {
            isConditionallyVisible = !!isWorkdayContingentEnabled?.enabled
          } else if (filterDefinition.id === 'program_team') {
            isConditionallyVisible = !!jobConfig?.is_program_team_visible
          } else if (filterDefinition.id === 'program_team_entity') {
            isConditionallyVisible = !!multiPmoEnabled?.enabled
          } else if (filterDefinition.id === 'vendor') {
            isConditionallyVisible = !!woGeneralConfig?.can_view_vendors
          } else if (filterDefinition.id === 'business_unit') {
            isConditionallyVisible = !!woGeneralConfig?.can_view_organization_unit
          } else if (filterDefinition.id === 'formula_group') {
            isConditionallyVisible = !!jobConfig?.can_view_calculation_pack
          }

          return isAllowedByRole && isConditionallyVisible
        })
      )
    }, [userAccount, jobConfig, woGeneralConfig, isWorkdayContingentEnabled, multiPmoEnabled])

    return {
      config,
      // TODO (vic) - I don't think the isLoaded stuff with FiltersProvider is working 100% correctly
      //  it seems like we can still snap a state when the data is missing and the filters do not load in
      //  the correct state - mostly this seems an issue on loading a list page with filters in url
      isLoaded: !jobLoading && !contingentLoading && !multiPmoLoading,
      savedFilterNamespace: UserPreferenceNamespace.WORK_ORDER
    }
  }
)

export function useFiltersDef() {
  const { data: woGeneralConfig } = useWorkOrderGeneralConfigQuery()
  const { data: userAccount } = useAccountMeQuery()
  return filtersDef.resolveConfig({ woGeneralConfig, userAccount })
}

export const workOrderListDataDef = defineListData({
  actionBarDef,
  filtersDef,
  queryOpts: workOrderListQueryOpts,
  bulkSelectStrategyOpts: bulkEligibleWorkOrderIdsOpts
})
